import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card, Row, Col, Typography, Button, Tag, Space, Statistic,
  Spin, message, Table, Modal, Form, Input, InputNumber,
  DatePicker, Select, Tabs, Alert, Popconfirm, Progress,
  Empty, Badge, Tooltip, Divider, Radio
} from 'antd';
import {
  BankOutlined,
  PlusOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CheckCircleFilled,
  CloseCircleOutlined,
  SyncOutlined,
  InfoCircleOutlined,
  DollarCircleOutlined,
  WalletOutlined,
  HistoryOutlined,
  ScheduleOutlined,
  RightOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../api';
import StaffLayout from './StaffLayout';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { TextArea } = Input;

// Helper to format currency
const fmtCurrency = (val) =>
  `₹${(Number(val) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Parse deductions safely
const parseDeductions = (adv) => {
  if (!adv) return [];
  let d = adv.deductions;
  while (typeof d === 'string') {
    try { d = JSON.parse(d); } catch (e) { break; }
  }
  return Array.isArray(d) ? d : [];
};

const ADVANCE_TYPES = [
  { label: 'Salary Advance', value: 'Salary Advance' },
  { label: 'Emergency Advance', value: 'Emergency Advance' },
  { label: 'Medical Advance', value: 'Medical Advance' },
  { label: 'Festival Advance', value: 'Festival Advance' },
  { label: 'Education Advance', value: 'Education Advance' },
  { label: 'General Advance', value: 'General Advance' }
];

const StaffAdvance = () => {
  const [loading, setLoading] = useState(false);
  const [advances, setAdvances] = useState([]);
  const [summary, setSummary] = useState({
    totalAdvanceAmount: 0,
    totalRecoveredAmount: 0,
    totalOutstandingBalance: 0,
    pendingRequestsCount: 0,
    totalCount: 0
  });

  const [activeTab, setActiveTab] = useState('advances');

  // Modal State
  const [applyModalVisible, setApplyModalVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  // Installment preview state inside modal
  const [repaymentType, setRepaymentType] = useState('single'); // 'single' or 'multiple'
  const [watchAmount, setWatchAmount] = useState(10000);
  const [watchInstallments, setWatchInstallments] = useState(2);
  const [watchStartMonth, setWatchStartMonth] = useState(dayjs().add(1, 'month'));

  // Details Modal State
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [selectedAdvance, setSelectedAdvance] = useState(null);

  // Fetch advances from backend
  const fetchAdvances = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/me/advances');
      if (res.data?.success) {
        setAdvances(res.data.advances || []);
        if (res.data.summary) {
          setSummary(res.data.summary);
        }
      }
    } catch (err) {
      console.error('Error fetching advances:', err);
      message.error(err.response?.data?.message || 'Failed to load advances');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAdvances();
  }, [fetchAdvances]);

  // Handle Submit New Advance Request
  const handleApplyAdvance = async (values) => {
    try {
      setSubmitting(true);
      const payload = {
        advanceType: values.advanceType,
        amount: values.amount,
        advanceDate: values.advanceDate ? values.advanceDate.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
        reason: values.reason,
        deductionMonth: values.startMonth ? values.startMonth.format('YYYY-MM') : dayjs().add(1, 'month').format('YYYY-MM'),
        installmentsCount: repaymentType === 'multiple' ? (values.installmentsCount || 2) : 1
      };

      const res = await api.post('/me/advances', payload);
      if (res.data?.success) {
        message.success('Advance request submitted successfully for Admin approval!');
        setApplyModalVisible(false);
        form.resetFields();
        fetchAdvances();
      } else {
        message.error(res.data?.message || 'Failed to submit advance request');
      }
    } catch (err) {
      console.error('Error applying advance:', err);
      message.error(err.response?.data?.message || 'Error submitting advance request');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle Cancel Pending Request
  const handleCancelAdvance = async (id) => {
    try {
      const res = await api.delete(`/me/advances/${id}`);
      if (res.data?.success) {
        message.success('Advance request cancelled successfully');
        fetchAdvances();
      } else {
        message.error(res.data?.message || 'Failed to cancel request');
      }
    } catch (err) {
      console.error('Error cancelling advance:', err);
      message.error(err.response?.data?.message || 'Failed to cancel request');
    }
  };

  // Consolidated Repayment Schedule across all advances
  const allInstallments = useMemo(() => {
    const list = [];
    advances.forEach((adv) => {
      const deductions = parseDeductions(adv);
      if (deductions.length > 0) {
        deductions.forEach((d, idx) => {
          list.push({
            key: `${adv.id}_${idx}`,
            advanceId: adv.id,
            advanceNotes: adv.notes || 'Advance',
            advanceDate: adv.advanceDate,
            month: d.month,
            amount: Number(d.amount || 0),
            status: d.status || 'pending',
            totalAdvance: Number(adv.amount || 0)
          });
        });
      } else if (adv.deductionMonth) {
        list.push({
          key: `${adv.id}_single`,
          advanceId: adv.id,
          advanceNotes: adv.notes || 'Advance',
          advanceDate: adv.advanceDate,
          month: adv.deductionMonth,
          amount: Number(adv.amount || 0),
          status: adv.status === 'deducted' ? 'deducted' : 'pending',
          totalAdvance: Number(adv.amount || 0)
        });
      }
    });

    return list.sort((a, b) => (a.month > b.month ? 1 : -1));
  }, [advances]);

  // Columns for My Advances Table
  const advanceColumns = [
    {
      title: 'Advance Type & Details',
      key: 'type',
      render: (_, r) => {
        // extract [Type] from notes if present
        let typeName = 'Salary Advance';
        let cleanReason = r.notes || '';
        const match = (r.notes || '').match(/^\[(.*?)\]\s*(.*)$/);
        if (match) {
          typeName = match[1];
          cleanReason = match[2];
        }

        return (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <Tag color="geekblue" style={{ borderRadius: 6, fontWeight: 700, margin: 0 }}>
                {typeName}
              </Tag>
              <Text type="secondary" style={{ fontSize: 12 }}>
                #{r.id}
              </Text>
            </div>
            <div style={{ fontSize: 13, color: '#334155', fontWeight: 500 }}>
              Applied: {dayjs(r.advanceDate).format('DD MMM YYYY')}
            </div>
            {cleanReason && (
              <div style={{ fontSize: 12, color: '#64748b', fontStyle: 'italic', marginTop: 2 }}>
                "{cleanReason}"
              </div>
            )}
          </div>
        );
      }
    },
    {
      title: 'Amount Breakdown',
      key: 'amounts',
      render: (_, r) => {
        const total = Number(r.amount || 0);
        const recovered = Number(r.recoveredAmount || 0);
        const outstanding = Number(r.outstandingBalance || 0);
        const percent = total > 0 ? Math.round((recovered / total) * 100) : 0;

        return (
          <div style={{ minWidth: 190 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ fontSize: 12, color: '#64748b' }}>Requested:</span>
              <strong style={{ fontSize: 13, color: '#0f172a' }}>{fmtCurrency(total)}</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ fontSize: 12, color: '#16a34a' }}>Recovered:</span>
              <span style={{ fontSize: 12, fontWeight: 600, color: '#16a34a' }}>{fmtCurrency(recovered)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ fontSize: 12, color: '#dc2626' }}>Outstanding:</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#dc2626' }}>{fmtCurrency(outstanding)}</span>
            </div>
            <Progress
              percent={percent}
              size="small"
              status={percent === 100 ? 'success' : 'active'}
              strokeColor={{ '0%': '#2563eb', '100%': '#10b981' }}
            />
          </div>
        );
      }
    },
    {
      title: 'Repayment Plan',
      key: 'plan',
      render: (_, r) => {
        const deductions = parseDeductions(r);
        if (deductions.length === 0) {
          return (
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#334155' }}>
                Single Month Recovery
              </div>
              <Tag color="cyan" style={{ borderRadius: 6, marginTop: 4 }}>
                {dayjs(r.deductionMonth, 'YYYY-MM').format('MMM YYYY')}
              </Tag>
            </div>
          );
        }

        const completedCount = deductions.filter(d => d.status === 'deducted').length;
        return (
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#334155' }}>
              {deductions.length} Monthly Installments
            </div>
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
              {fmtCurrency(deductions[0]?.amount)} / month
            </div>
            <div style={{ marginTop: 6 }}>
              <Tag color="purple" style={{ borderRadius: 6 }}>
                {completedCount} of {deductions.length} Deducted
              </Tag>
            </div>
          </div>
        );
      }
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (st, r) => {
        if (st === 'pending') {
          return (
            <Tag color="warning" icon={<ClockCircleOutlined />} style={{ borderRadius: 6, fontWeight: 700, padding: '3px 8px' }}>
              Pending Approval
            </Tag>
          );
        }
        if (st === 'cancelled') {
          return (
            <Tag color="default" icon={<CloseCircleOutlined />} style={{ borderRadius: 6, fontWeight: 600, padding: '3px 8px' }}>
              Cancelled
            </Tag>
          );
        }
        if (st === 'deducted' || Number(r.outstandingBalance) === 0) {
          return (
            <Tag color="success" icon={<CheckCircleFilled />} style={{ borderRadius: 6, fontWeight: 700, padding: '3px 8px' }}>
              Fully Recovered
            </Tag>
          );
        }
        return (
          <Tag color="blue" icon={<CheckCircleOutlined />} style={{ borderRadius: 6, fontWeight: 700, padding: '3px 8px' }}>
            Active / Approved
          </Tag>
        );
      }
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, r) => {
        return (
          <Space size="middle">
            <Button
              size="small"
              onClick={() => {
                setSelectedAdvance(r);
                setDetailsModalVisible(true);
              }}
              style={{ borderRadius: 6, fontSize: 12 }}
            >
              View Plan
            </Button>

            {r.status === 'pending' && (
              <Popconfirm
                title="Cancel this advance request?"
                description="Are you sure you want to withdraw this request?"
                onConfirm={() => handleCancelAdvance(r.id)}
                okText="Yes, Cancel"
                cancelText="No"
                okButtonProps={{ danger: true }}
              >
                <Button size="small" danger style={{ borderRadius: 6, fontSize: 12 }}>
                  Cancel
                </Button>
              </Popconfirm>
            )}
          </Space>
        );
      }
    }
  ];

  // Columns for Repayment Schedule Table
  const installmentColumns = [
    {
      title: 'Deduction Month',
      dataIndex: 'month',
      key: 'month',
      render: (m) => (
        <div>
          <strong style={{ color: '#0f172a', fontSize: 13 }}>
            {dayjs(m, 'YYYY-MM').format('MMMM YYYY')}
          </strong>
        </div>
      )
    },
    {
      title: 'Advance Ref & Type',
      key: 'ref',
      render: (_, r) => (
        <div>
          <span style={{ fontSize: 12, fontWeight: 600, color: '#334155' }}>
            Advance #{r.advanceId}
          </span>
          <div style={{ fontSize: 11, color: '#64748b' }}>
            {r.advanceNotes}
          </div>
        </div>
      )
    },
    {
      title: 'Installment Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amt) => (
        <span style={{ fontWeight: 700, color: '#0f172a', fontSize: 14 }}>
          {fmtCurrency(amt)}
        </span>
      )
    },
    {
      title: 'Deduction Status',
      dataIndex: 'status',
      key: 'status',
      render: (st) => {
        if (st === 'deducted') {
          return (
            <Tag color="green" icon={<CheckCircleFilled />} style={{ borderRadius: 6, fontWeight: 600 }}>
              Deducted via Payroll
            </Tag>
          );
        }
        return (
          <Tag color="blue" icon={<ClockCircleOutlined />} style={{ borderRadius: 6, fontWeight: 500 }}>
            Scheduled for Payroll
          </Tag>
        );
      }
    }
  ];

  return (
    <StaffLayout>
      <div style={{ paddingBottom: 40 }}>
        {/* 1. HEADER CARD */}
        <Card
          style={{
            borderRadius: 16,
            border: '1px solid #e2e8f0',
            marginBottom: 20,
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
          }}
          bodyStyle={{ padding: 24 }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
            <Space align="center" size={14}>
              <div style={{
                width: 48,
                height: 48,
                borderRadius: 12,
                background: '#eff6ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#2563eb',
                fontSize: 24
              }}>
                <BankOutlined />
              </div>
              <div>
                <Title level={4} style={{ margin: 0, color: '#0f172a', fontWeight: 700 }}>
                  Advances
                </Title>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  Apply for advance salary, track approvals, monthly installments & outstanding balance
                </Text>
              </div>
            </Space>

            <Space wrap size="middle">
              <Button
                icon={<SyncOutlined />}
                onClick={fetchAdvances}
                loading={loading}
                style={{ borderRadius: 8, height: 38 }}
              >
                Refresh
              </Button>

              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  form.resetFields();
                  setWatchAmount(10000);
                  setWatchInstallments(2);
                  setWatchStartMonth(dayjs().add(1, 'month'));
                  setRepaymentType('single');
                  setApplyModalVisible(true);
                }}
                style={{
                  borderRadius: 8,
                  height: 38,
                  background: '#2563eb',
                  borderColor: '#2563eb',
                  fontWeight: 600,
                  boxShadow: '0 2px 4px rgba(37, 99, 235, 0.15)'
                }}
              >
                Apply for Advance
              </Button>
            </Space>
          </div>
        </Card>

        {/* 2. TOP METRIC SUMMARY CARDS */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 16,
          marginBottom: 20
        }}>
          {/* Outstanding Balance */}
          <Card
            style={{
              borderRadius: 16,
              border: '1px solid #fecaca',
              background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
            }}
            bodyStyle={{ padding: '20px 22px' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <Text strong style={{ color: '#b91c1c', fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Outstanding Balance
              </Text>
              <span style={{ fontSize: 20 }}>💳</span>
            </div>
            <div style={{ fontSize: 28, fontWeight: 800, color: '#7f1d1d', lineHeight: 1.2 }}>
              {fmtCurrency(summary.totalOutstandingBalance)}
            </div>
            <div style={{ marginTop: 8, fontSize: 12, color: '#991b1b', fontWeight: 500 }}>
              Amount remaining to be deducted from payroll
            </div>
          </Card>

          {/* Total Advance Taken */}
          <Card
            style={{
              borderRadius: 16,
              border: '1px solid #bfdbfe',
              background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
            }}
            bodyStyle={{ padding: '20px 22px' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <Text strong style={{ color: '#1d4ed8', fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Total Advance Taken
              </Text>
              <span style={{ fontSize: 20 }}>💰</span>
            </div>
            <div style={{ fontSize: 28, fontWeight: 800, color: '#1e3a8a', lineHeight: 1.2 }}>
              {fmtCurrency(summary.totalAdvanceAmount)}
            </div>
            <div style={{ marginTop: 8, fontSize: 12, color: '#1e40af', fontWeight: 500 }}>
              Across {summary.totalCount} advance records
            </div>
          </Card>

          {/* Total Recovered */}
          <Card
            style={{
              borderRadius: 16,
              border: '1px solid #bbf7d0',
              background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
            }}
            bodyStyle={{ padding: '20px 22px' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <Text strong style={{ color: '#15803d', fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Total Recovered
              </Text>
              <span style={{ fontSize: 20 }}>📉</span>
            </div>
            <div style={{ fontSize: 28, fontWeight: 800, color: '#14532d', lineHeight: 1.2 }}>
              {fmtCurrency(summary.totalRecoveredAmount)}
            </div>
            <div style={{ marginTop: 8, fontSize: 12, color: '#166534', fontWeight: 500 }}>
              Deducted automatically via monthly salary
            </div>
          </Card>

          {/* Pending Approval Requests */}
          <Card
            style={{
              borderRadius: 16,
              border: '1px solid #fde68a',
              background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
            }}
            bodyStyle={{ padding: '20px 22px' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <Text strong style={{ color: '#b45309', fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Pending Review
              </Text>
              <span style={{ fontSize: 20 }}>⏳</span>
            </div>
            <div style={{ fontSize: 28, fontWeight: 800, color: '#78350f', lineHeight: 1.2 }}>
              {summary.pendingRequestsCount} Request(s)
            </div>
            <div style={{ marginTop: 8, fontSize: 12, color: '#92400e', fontWeight: 500 }}>
              Awaiting admin / HR verification
            </div>
          </Card>
        </div>

        {/* 3. MAIN TABS CARD */}
        <Card
          style={{
            borderRadius: 16,
            border: '1px solid #e2e8f0',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
          }}
          bodyStyle={{ padding: 24 }}
        >
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={[
              {
                key: 'advances',
                label: (
                  <span style={{ fontWeight: 600 }}>
                    <BankOutlined />
                    My Advances & Applications
                    {summary.pendingRequestsCount > 0 && (
                      <Badge count={summary.pendingRequestsCount} style={{ marginLeft: 8, backgroundColor: '#f59e0b' }} />
                    )}
                  </span>
                ),
                children: (
                  <Table
                    columns={advanceColumns}
                    dataSource={advances}
                    rowKey="id"
                    loading={loading}
                    pagination={{ pageSize: 8, showSizeChanger: true }}
                    locale={{ emptyText: 'No advance requests or records found' }}
                    style={{ borderRadius: 8 }}
                    scroll={{ x: 900 }}
                  />
                )
              },
              {
                key: 'schedule',
                label: (
                  <span style={{ fontWeight: 600 }}>
                    <ScheduleOutlined />
                    Repayment Schedule & EMIs ({allInstallments.length})
                  </span>
                ),
                children: (
                  <div>
                    <div style={{ marginBottom: 16 }}>
                      <Alert
                        message="Monthly installments are automatically synchronized and deducted from your monthly salary payslip by payroll cycles."
                        type="info"
                        showIcon
                        style={{ borderRadius: 10 }}
                      />
                    </div>
                    <Table
                      columns={installmentColumns}
                      dataSource={allInstallments}
                      pagination={{ pageSize: 10 }}
                      locale={{ emptyText: 'No scheduled installments found' }}
                      style={{ borderRadius: 8 }}
                      size="middle"
                    />
                  </div>
                )
              }
            ]}
          />
        </Card>

        {/* 4. APPLY ADVANCE MODAL */}
        <Modal
          title={
            <Space>
              <BankOutlined style={{ color: '#2563eb', fontSize: 18 }} />
              <span style={{ fontWeight: 700 }}>Apply for Salary Advance</span>
            </Space>
          }
          open={applyModalVisible}
          onCancel={() => {
            if (!submitting) {
              setApplyModalVisible(false);
            }
          }}
          onOk={() => form.submit()}
          confirmLoading={submitting}
          okText="Submit Request"
          okButtonProps={{
            style: {
              background: '#2563eb',
              borderColor: '#2563eb',
              borderRadius: 6,
              fontWeight: 600
            }
          }}
          cancelButtonProps={{ borderRadius: 6 }}
          width={560}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleApplyAdvance}
            initialValues={{
              advanceType: 'Salary Advance',
              amount: 10000,
              advanceDate: dayjs(),
              startMonth: dayjs().add(1, 'month'),
              installmentsCount: 2
            }}
            onValuesChange={(_, all) => {
              if (all.amount) setWatchAmount(Number(all.amount));
              if (all.installmentsCount) setWatchInstallments(Number(all.installmentsCount));
              if (all.startMonth) setWatchStartMonth(all.startMonth);
            }}
            style={{ marginTop: 16 }}
          >
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="advanceType"
                  label="Advance Type"
                  rules={[{ required: true, message: 'Please select advance type' }]}
                >
                  <Select style={{ borderRadius: 6 }}>
                    {ADVANCE_TYPES.map(t => (
                      <Option key={t.value} value={t.value}>{t.label}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item
                  name="amount"
                  label="Amount (₹)"
                  rules={[
                    { required: true, message: 'Please enter amount' },
                    { type: 'number', min: 500, message: 'Amount must be at least ₹500' }
                  ]}
                >
                  <InputNumber
                    style={{ width: '100%', borderRadius: 6 }}
                    formatter={(val) => `₹ ${val}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={(val) => val.replace(/₹\s?|(,*)/g, '')}
                    placeholder="e.g. 20000"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="advanceDate"
                  label="Required By / Advance Date"
                  rules={[{ required: true, message: 'Please select date' }]}
                >
                  <DatePicker style={{ width: '100%', borderRadius: 6 }} format="DD MMM YYYY" />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item
                  name="startMonth"
                  label="Deduction Start Month"
                  rules={[{ required: true, message: 'Please select month' }]}
                >
                  <DatePicker picker="month" style={{ width: '100%', borderRadius: 6 }} format="MMM YYYY" />
                </Form.Item>
              </Col>
            </Row>

            {/* Repayment Option: Single Month vs Installments */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontWeight: 600, fontSize: 13, marginBottom: 8, color: '#334155' }}>
                Repayment Preference
              </label>
              <Radio.Group
                value={repaymentType}
                onChange={(e) => setRepaymentType(e.target.value)}
                buttonStyle="solid"
                style={{ width: '100%', display: 'flex' }}
              >
                <Radio.Button value="single" style={{ flex: 1, textAlign: 'center', borderRadius: '6px 0 0 6px' }}>
                  Single Month Deduction
                </Radio.Button>
                <Radio.Button value="multiple" style={{ flex: 1, textAlign: 'center', borderRadius: '0 6px 6px 0' }}>
                  Multiple Monthly Installments (EMI)
                </Radio.Button>
              </Radio.Group>
            </div>

            {repaymentType === 'multiple' && (
              <Form.Item
                name="installmentsCount"
                label="Number of Monthly Installments"
                rules={[{ required: true, message: 'Please select installments' }]}
              >
                <Select style={{ borderRadius: 6 }}>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(num => (
                    <Option key={num} value={num}>
                      {num === 1 ? '1 Month' : `${num} Months`} ({fmtCurrency(Math.round(watchAmount / num))} / month)
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            )}

            {/* Live Calculation Preview Box */}
            <div style={{
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: 10,
              padding: '12px 16px',
              marginBottom: 16
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 12, color: '#64748b' }}>Requested Advance:</span>
                <strong style={{ color: '#0f172a' }}>{fmtCurrency(watchAmount)}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 12, color: '#64748b' }}>Repayment Terms:</span>
                <strong style={{ color: '#2563eb' }}>
                  {repaymentType === 'single'
                    ? `Full deduction in ${watchStartMonth?.format('MMM YYYY') || 'next month'}`
                    : `${watchInstallments} monthly installments of approx. ${fmtCurrency(Math.round(watchAmount / watchInstallments))} / month`}
                </strong>
              </div>
            </div>

            <Form.Item
              name="reason"
              label="Reason for Advance"
              rules={[
                { required: true, message: 'Please provide reason for advance' },
                { min: 5, message: 'Reason must be at least 5 characters' }
              ]}
            >
              <TextArea
                rows={3}
                placeholder="Please describe why advance is required (e.g. medical emergency, household expenses, etc.)"
                style={{ borderRadius: 6 }}
              />
            </Form.Item>
          </Form>
        </Modal>

        {/* 5. VIEW ADVANCE PLAN & DETAILS MODAL */}
        <Modal
          title={
            <Space>
              <BankOutlined style={{ color: '#2563eb' }} />
              <span style={{ fontWeight: 700 }}>Advance Details & Repayment Plan</span>
            </Space>
          }
          open={detailsModalVisible}
          onCancel={() => setDetailsModalVisible(false)}
          footer={[
            <Button key="close" onClick={() => setDetailsModalVisible(false)} style={{ borderRadius: 6 }}>
              Close
            </Button>
          ]}
          width={580}
        >
          {selectedAdvance && (
            <div style={{ marginTop: 12 }}>
              <div style={{
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: 12,
                padding: '16px',
                marginBottom: 16
              }}>
                <Row gutter={[16, 12]}>
                  <Col span={12}>
                    <Text type="secondary" style={{ fontSize: 12 }}>Advance ID</Text>
                    <div><strong style={{ fontSize: 14 }}>#{selectedAdvance.id}</strong></div>
                  </Col>
                  <Col span={12}>
                    <Text type="secondary" style={{ fontSize: 12 }}>Application Date</Text>
                    <div><strong>{dayjs(selectedAdvance.advanceDate).format('DD MMM YYYY')}</strong></div>
                  </Col>
                  <Col span={12}>
                    <Text type="secondary" style={{ fontSize: 12 }}>Total Requested</Text>
                    <div><strong style={{ color: '#2563eb', fontSize: 16 }}>{fmtCurrency(selectedAdvance.amount)}</strong></div>
                  </Col>
                  <Col span={12}>
                    <Text type="secondary" style={{ fontSize: 12 }}>Current Status</Text>
                    <div>
                      {selectedAdvance.status === 'pending' ? (
                        <Tag color="warning">Pending Approval</Tag>
                      ) : selectedAdvance.status === 'deducted' ? (
                        <Tag color="green">Fully Deducted</Tag>
                      ) : selectedAdvance.status === 'cancelled' ? (
                        <Tag color="default">Cancelled</Tag>
                      ) : (
                        <Tag color="blue">Active / Approved</Tag>
                      )}
                    </div>
                  </Col>
                  <Col span={24}>
                    <Text type="secondary" style={{ fontSize: 12 }}>Notes / Reason</Text>
                    <div style={{ fontStyle: 'italic', color: '#334155' }}>
                      "{selectedAdvance.notes || 'None'}"
                    </div>
                  </Col>
                </Row>
              </div>

              <Title level={5} style={{ margin: '0 0 12px 0' }}>Installment Recovery Schedule</Title>

              {parseDeductions(selectedAdvance).length === 0 ? (
                <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', padding: 14, borderRadius: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <strong style={{ color: '#0f172a' }}>
                        Single Deduction in {dayjs(selectedAdvance.deductionMonth, 'YYYY-MM').format('MMMM YYYY')}
                      </strong>
                    </div>
                    <div>
                      <Tag color={selectedAdvance.status === 'deducted' ? 'green' : 'blue'}>
                        {selectedAdvance.status === 'deducted' ? 'Deducted' : 'Scheduled'}
                      </Tag>
                      <strong>{fmtCurrency(selectedAdvance.amount)}</strong>
                    </div>
                  </div>
                </div>
              ) : (
                <Table
                  size="small"
                  pagination={false}
                  dataSource={parseDeductions(selectedAdvance).map((d, i) => ({ ...d, key: i }))}
                  columns={[
                    {
                      title: 'Month',
                      dataIndex: 'month',
                      key: 'month',
                      render: (m) => <strong>{dayjs(m, 'YYYY-MM').format('MMM YYYY')}</strong>
                    },
                    {
                      title: 'Installment Amount',
                      dataIndex: 'amount',
                      key: 'amount',
                      render: (amt) => fmtCurrency(amt)
                    },
                    {
                      title: 'Status',
                      dataIndex: 'status',
                      key: 'status',
                      render: (st) => (
                        <Tag color={st === 'deducted' ? 'green' : 'blue'}>
                          {st === 'deducted' ? 'Deducted' : 'Scheduled'}
                        </Tag>
                      )
                    }
                  ]}
                  style={{ borderRadius: 8 }}
                />
              )}
            </div>
          )}
        </Modal>
      </div>
    </StaffLayout>
  );
};

export default StaffAdvance;
